import { useMutation, useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query';
import {
    fetchRoles,
    fetchRoleMembers,
    createRole,
    updateRole,
    deleteRole,
    addRoleMember,
    removeRoleMember,
    type CreateRoleInput,
    type RoleScope,
    type UpdateRoleInput
} from '../services/roles';
import { fetchTeamUsers } from '../services/users';
import { TEAM_MEMBERS_KEY, TEAM_USERS_KEY, TEAMS_KEY, TEAMS_LIST_KEY } from './queryKeys';

const TEAMS_STALE_TIME = 60_000;
const TEAM_MEMBERS_STALE_TIME = 60_000;
// Candidate pool for the member picker - code-adjacent (any admin user in the
// scope), not state that changes often within a session.
const TEAM_USERS_STALE_TIME = 5 * 60 * 1000;

export function useTeams(scope?: RoleScope) {
    const { data, isLoading, isError, error, refetch } = useQuery({
        queryKey: TEAMS_LIST_KEY(scope),
        queryFn: () => fetchRoles(scope),
        staleTime: TEAMS_STALE_TIME
    });

    return { teams: data ?? [], isLoading, isError, error, refresh: refetch };
}

export function useTeamMembers(teamId: string | null) {
    const { data, isLoading, isError, error } = useQuery({
        queryKey: TEAM_MEMBERS_KEY(teamId ?? ''),
        queryFn: () => fetchRoleMembers(teamId as string),
        enabled: Boolean(teamId),
        staleTime: TEAM_MEMBERS_STALE_TIME
    });

    return { members: data ?? [], isLoading: isLoading && Boolean(teamId), isError, error };
}

export function useTeamUsers(scope: RoleScope | null) {
    const { data, isLoading, isError, error } = useQuery({
        queryKey: TEAM_USERS_KEY(scope ?? ''),
        queryFn: () => fetchTeamUsers(scope as RoleScope),
        enabled: Boolean(scope),
        staleTime: TEAM_USERS_STALE_TIME
    });

    return { users: data ?? [], isLoading: isLoading && Boolean(scope), isError, error };
}

// Every team mutation invalidates the whole [TEAMS_KEY] prefix rather than
// picking specific sub-keys: TEAMS_LIST_KEY, TEAM_MEMBERS_KEY and useRoles.ts's
// conversation-assignment keys all nest under it, so one call keeps the Teams
// table, an open team's member list, and chat assignment in sync.
function invalidateTeamCaches(queryClient: QueryClient): void {
    void queryClient.invalidateQueries({ queryKey: [TEAMS_KEY] });
}

export function useCreateTeam() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (input: CreateRoleInput) => createRole(input),
        onSuccess: () => invalidateTeamCaches(queryClient)
    });
}

export function useUpdateTeam() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: ({ teamId, input }: { teamId: string; input: UpdateRoleInput }) =>
            updateRole(teamId, input),
        onSuccess: () => invalidateTeamCaches(queryClient)
    });
}

export function useDeleteTeam() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (teamId: string) => deleteRole(teamId),
        onSuccess: () => invalidateTeamCaches(queryClient)
    });
}

export function useAddTeamMember() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: ({ teamId, userId }: { teamId: string; userId: string }) =>
            addRoleMember(teamId, userId),
        onSuccess: () => invalidateTeamCaches(queryClient)
    });
}

export function useRemoveTeamMember() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: ({ teamId, userId }: { teamId: string; userId: string }) =>
            removeRoleMember(teamId, userId),
        onSuccess: () => invalidateTeamCaches(queryClient)
    });
}
